/**
 * Time Period Grouping Utility
 *
 * Groups documents by time period for Flow View display.
 * Supports: Today, Yesterday, This Week, This Month, Older
 *
 * @module features/dashboard/utils/groupByTimePeriod
 * @see SPEC-016 Requirement 5.1
 * @see SPEC-017 Task 6.2 - i18n integration
 */

import type { DocListItemInput } from '@linkode/shared';
import { formatRelativeTime, formatShortDate } from '../../../i18n/formatters';
import type { SupportedDateLocale } from '../../../i18n/formatters';

// ============================================================================
// Types
// ============================================================================

/**
 * Time period identifiers for grouping
 */
export type TimePeriod = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'older';

/**
 * Display labels for time periods
 */
export const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  older: 'Older',
};

/**
 * Order of time periods for display
 */
export const TIME_PERIOD_ORDER: TimePeriod[] = [
  'today',
  'yesterday',
  'thisWeek',
  'thisMonth',
  'older',
];

/**
 * A group of documents with a time period label
 */
export interface TimeGroup<T = DocListItemInput> {
  period: TimePeriod;
  label: string;
  docs: T[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the start of a day (midnight) for a given date
 */
function getStartOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get the start of the week (Sunday) for a given date
 */
function getStartOfWeek(date: Date): Date {
  const result = getStartOfDay(date);
  const day = result.getDay();
  result.setDate(result.getDate() - day);
  return result;
}

/**
 * Get the start of the month for a given date
 */
function getStartOfMonth(date: Date): Date {
  const result = new Date(date);
  result.setDate(1);
  result.setHours(0, 0, 0, 0);
  return result;
}

// ============================================================================
// Main Functions
// ============================================================================

/**
 * Determine which time period a timestamp belongs to
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @param now - Current date (optional, defaults to new Date())
 * @returns The time period the timestamp belongs to
 *
 * @example
 * ```ts
 * const period = getTimePeriod(Date.now()); // 'today'
 * const period = getTimePeriod(Date.now() - 86400000); // 'yesterday'
 * ```
 *
 * @see Requirement 5.1
 */
export function getTimePeriod(timestamp: number, now: Date = new Date()): TimePeriod {
  const date = new Date(timestamp);

  const todayStart = getStartOfDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const weekStart = getStartOfWeek(now);
  const monthStart = getStartOfMonth(now);

  // Check if timestamp is today
  if (date >= todayStart) {
    return 'today';
  }

  // Check if timestamp is yesterday
  if (date >= yesterdayStart) {
    return 'yesterday';
  }

  // Check if timestamp is this week (but not today or yesterday)
  if (date >= weekStart) {
    return 'thisWeek';
  }

  // Check if timestamp is this month (but not this week)
  if (date >= monthStart) {
    return 'thisMonth';
  }

  // Everything else is older
  return 'older';
}

/**
 * Group documents by time period based on their updatedAt timestamp
 *
 * @param docs - Array of documents to group
 * @param now - Current date (optional, defaults to new Date())
 * @returns Array of time groups, ordered from most recent to oldest
 *
 * @example
 * ```ts
 * const groups = groupByTimePeriod(documents);
 * // [
 * //   { period: 'today', label: 'Today', docs: [...] },
 * //   { period: 'yesterday', label: 'Yesterday', docs: [...] },
 * //   { period: 'thisWeek', label: 'This Week', docs: [...] },
 * //   ...
 * // ]
 * ```
 *
 * @see Requirement 5.1
 */
export function groupByTimePeriod<T extends { updatedAt: number }>(
  docs: T[],
  now: Date = new Date()
): TimeGroup<T>[] {
  // Initialize groups map
  const groupsMap = new Map<TimePeriod, T[]>();

  // Initialize all periods with empty arrays
  for (const period of TIME_PERIOD_ORDER) {
    groupsMap.set(period, []);
  }

  // Group documents by time period
  for (const doc of docs) {
    const period = getTimePeriod(doc.updatedAt, now);
    const group = groupsMap.get(period);
    if (group) {
      group.push(doc);
    }
  }

  // Convert to array and filter out empty groups
  const result: TimeGroup<T>[] = [];

  for (const period of TIME_PERIOD_ORDER) {
    const docs = groupsMap.get(period);
    if (docs && docs.length > 0) {
      result.push({
        period,
        label: TIME_PERIOD_LABELS[period],
        docs,
      });
    }
  }

  return result;
}

/**
 * Get a human-readable relative time string for a timestamp
 * Uses i18n formatters for locale-aware output.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @param locale - Locale code (defaults to 'en')
 * @param now - Current date (optional, defaults to new Date())
 * @returns Human-readable relative time string
 *
 * @example
 * ```ts
 * getRelativeTime(Date.now() - 60000, 'en'); // '1 minute ago'
 * getRelativeTime(Date.now() - 3600000, 'zh-TW'); // '1 小時前'
 * ```
 *
 * @see SPEC-017 Task 6.2
 */
export function getRelativeTime(
  timestamp: number,
  locale: SupportedDateLocale = 'en',
  now: Date = new Date()
): string {
  const date = new Date(timestamp);
  const diffMs = now.getTime() - timestamp;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // For dates older than 7 days, use short date format
  if (diffDays >= 7) {
    return formatShortDate(date, locale);
  }

  // For recent dates, use relative time format
  return formatRelativeTime(date, locale, now);
}
